/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useMutation } from '@apollo/client';
import { Box, Button, Flex, Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay, useToast } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import * as moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import CompanyFormField from '../../../components/form/Company/CompanyFormField';
import CompanyTypeFormField from '../../../components/form/Company/CompanyTypeFormField';
import CompanyReportTypeFormField from '../../../components/form/CompanyReportTypeFormField';
import DateInputOrSelectFormField from '../../../components/form/DateInputOrSelectFormField';
import FileFormField from '../../../components/form/FileFormField';
import SelectValueFormField from '../../../components/form/SelectValueFormField';
import c from '../../../constant';
import useMoment from '../../../hooks/useMoment';
import useStore from '../../../store';
import { mappingKhmerNumber } from '../../../utils/market-development';
import {
	annuallyFileReport,
	mdMonthlyClaim,
	mdMonthlyDistribution,
	mdMonthlySale,
	mdQuarterlyClaim,
	mdQuarterlySale,
	monthlyFileReport,
	periodMapping,
	quarterlyFileReport,
	sdQuarterlyFinance,
	sdQuarterlyInvestment,
	semesterlyFileReport,
} from '../Common/Constants';
import { getPermittedCompanyTypes, getPermittedReportTypes } from '../Common/Utils';

const M_CREATE = gql`
	mutation CreateReportDue($dto: CreateReportDueDto!) {
		createReportDue(dto: $dto) {
			id
		}
	}
`;

function Create({ onClose = () => null, onComplete = () => null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const { toDate } = useMoment();
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const [createReportDue, { loading }] = useMutation(M_CREATE);

	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				reportType: Yup.string().required('required'),
				companyType: Yup.string().nullable(),
				company: Yup.object()
					.test('fileSize', 'required', (value) => {
						return value?.companyId ? true : false;
					})
					.nullable(),
				year: Yup.mixed().when('reportType', {
					is: (reportType) =>
						reportType === monthlyFileReport?.value ||
						reportType === quarterlyFileReport?.value ||
						reportType === semesterlyFileReport?.value ||
						reportType === annuallyFileReport?.value,
					then: Yup.string(),
					otherwise: Yup.string().required('required'),
				}),
				month: Yup.mixed().when('reportType', {
					is: (reportType) =>
						reportType === mdMonthlySale?.value || reportType === mdMonthlyClaim?.value || reportType === mdMonthlyDistribution?.value,
					then: Yup.string().required('required'),
					otherwise: Yup.string(),
				}),
				quarter: Yup.mixed().when('reportType', {
					is: (reportType) =>
						reportType === mdQuarterlySale?.value ||
						reportType === mdQuarterlyClaim?.value ||
						reportType === sdQuarterlyFinance?.value ||
						reportType === sdQuarterlyInvestment?.value,
					then: Yup.string().required('required'),
					otherwise: Yup.string(),
				}),
				report: Yup.mixed().when('reportType', {
					is: (reportType) =>
						reportType === monthlyFileReport?.value ||
						reportType === quarterlyFileReport?.value ||
						reportType === semesterlyFileReport?.value ||
						reportType === annuallyFileReport?.value,
					then: Yup.string().required('required'),
					otherwise: Yup.string(),
				}),
				dueDate: Yup.mixed().test('empty', 'required', (value) => {
					return value ? true : false;
				}),
				attachment: Yup.mixed().test('fileSize', 'over limit', (value) => {
					if (value) {
						return value?.size <= c.maxFileSize / 20;
					} else {
						return true;
					}
				}),
			}),
		[],
	);

	const reports = useMemo(() => {
		const resultReportType = getPermittedReportTypes(user);
		const mdReportTypes = resultReportType['md'] ?? [];
		const sdReportTypes = resultReportType['sd'] ?? [];
		const fileReportTypes = resultReportType['file'] ?? [];
		const localReports = [...mdReportTypes, ...sdReportTypes, ...fileReportTypes];
		return localReports?.map((localReport) => ({ label: lang === 'kh' ? localReport?.labelKh : localReport?.labelEn, value: localReport?.value }));
	}, [user, lang]);

	const getCompanyTypes = (user, reportValue) => {
		const resultCompanyTypes = getPermittedCompanyTypes(user);
		const resultReportTypes = getPermittedReportTypes(user);
		const mdCompanyTypes = resultCompanyTypes['md'] ?? [];
		const sdCompanyTypes = resultCompanyTypes['sd'] ?? [];
		const fileCompanyTypes = resultCompanyTypes['file'] ?? [];
		const mdReportTypes = resultReportTypes['md'] ?? [];
		const sdReportTypes = resultReportTypes['sd'] ?? [];
		const fileReportTypes = resultReportTypes['file'] ?? [];
		if (mdReportTypes?.some((type) => type?.value === reportValue)) {
			return c.insuranceCompanyTypes.filter((insuranceCompanyType) => mdCompanyTypes?.includes(insuranceCompanyType?.value));
		} else if (sdReportTypes?.some((type) => type?.value === reportValue)) {
			return c.insuranceCompanyTypes.filter((insuranceCompanyType) => sdCompanyTypes?.includes(insuranceCompanyType?.value));
		} else if (fileReportTypes?.some((type) => type?.value === reportValue)) {
			return c.insuranceCompanyTypes.filter((insuranceCompanyType) => fileCompanyTypes?.includes(insuranceCompanyType?.value));
		}
		return [];
	};

	const submit = (values) => {
		const dto = {
			type: values?.reportType,
			year:
				values?.reportType === monthlyFileReport?.value ||
				values?.reportType === quarterlyFileReport?.value ||
				values?.reportType === semesterlyFileReport?.value ||
				values?.reportType === annuallyFileReport?.value
					? null
					: parseInt(values.year),
			typeValue:
				values?.reportType === mdMonthlySale?.value ||
				values?.reportType === mdMonthlyClaim?.value ||
				values?.reportType === mdMonthlyDistribution?.value
					? parseInt(values?.month)
					: values?.reportType === mdQuarterlySale?.value ||
						  values?.reportType === mdQuarterlyClaim?.value ||
						  values?.reportType === sdQuarterlyFinance?.value ||
						  values?.reportType === sdQuarterlyInvestment?.value
						? parseInt(values?.quarter)
						: null,
			dueDate: moment(toDate(values.dueDate, c.dateFormats)).endOf('day').toDate(),
			companyId: values?.company?.companyId,
			attachment: values?.attachment,
			attachmentFileSize: values?.attachment?.size,
			fileReportId:
				values?.reportType === monthlyFileReport?.value ||
				values?.reportType === quarterlyFileReport?.value ||
				values?.reportType === semesterlyFileReport?.value ||
				values?.reportType === annuallyFileReport?.value
					? values?.report
					: null,
		};
		createReportDue({ variables: { dto } })
			.then(({ data }) => {
				if (data?.createReportDue?.id) {
					toast({
						title: t('Success'),
						status: 'success',
						position: 'top-right',
						isClosable: true,
					});
					onComplete();
					onClose();
				}
			})
			.catch((error) => {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			});
	};

	return (
		<Modal closeOnEsc={!loading} closeOnOverlayClick={!loading} isOpen={true} onClose={onClose}>
			<ModalOverlay />
			<ModalContent borderRadius={c.borderRadius}>
				<ModalHeader pb={0} mb={0}>
					{t('Create')}
				</ModalHeader>
				<ModalCloseButton isDisabled={loading} />
				<ModalBody>
					<Formik
						initialValues={{
							reportType: '',
							companyType: '',
							company: { companyId: null },
							year: '',
							month: '',
							quarter: '',
							report: '',
							dueDate: null,
							attachment: null,
						}}
						validationSchema={validationSchema}
						onSubmit={submit}
					>
						{({ values, errors, props }) => {
							return (
								<Form>
									<Box mt="2">
										<>
											<SelectValueFormField
												placeholder={t('Report Type')}
												name="reportType"
												label="Report Type"
												options={reports}
												required={true}
											/>
										</>
										{values?.reportType && (
											<>
												<>
													<CompanyTypeFormField
														{...props}
														name="companyType"
														companyFormName="company"
														label="Company Type"
														placeholder={t('Company Type')}
														types={getCompanyTypes(user, values?.reportType)}
														required={false}
													/>
												</>
												<>
													<CompanyFormField
														{...props}
														name="company"
														companyTypeFormName="companyType"
														label="Company"
														types={getCompanyTypes(user, values?.reportType)?.map((e) => e?.value)}
														required={true}
													/>
												</>
												<>
													{values?.reportType !== monthlyFileReport?.value &&
														values?.reportType !== quarterlyFileReport?.value &&
														values?.reportType !== semesterlyFileReport?.value &&
														values?.reportType !== annuallyFileReport?.value && (
															<SelectValueFormField
																{...props}
																placeholder={t('Year')}
																name="year"
																label="Year"
																options={[0].reduce((p, c) => {
																	let lp = p;
																	let i = 2021;
																	while (i <= moment().year()) {
																		lp.push({ label: lang === 'kh' ? mappingKhmerNumber(i) : i, value: i });
																		i = i + 1;
																	}
																	return lp?.reverse();
																}, [])}
																required={true}
															/>
														)}
												</>
												<>
													{(values?.reportType === monthlyFileReport?.value ||
														values?.reportType === quarterlyFileReport?.value ||
														values?.reportType === semesterlyFileReport?.value ||
														values?.reportType === annuallyFileReport?.value) && (
														<CompanyReportTypeFormField
															{...props}
															period={periodMapping[values?.reportType]}
															placeholder={t('Report')}
															name="report"
															label="Report"
															required={true}
														/>
													)}
												</>
												<>
													{(values.reportType === mdMonthlySale?.value ||
														values.reportType === mdMonthlyClaim?.value ||
														values.reportType === mdMonthlyDistribution?.value) && (
														<SelectValueFormField
															{...props}
															placeholder={t('Month')}
															name="month"
															label="Month"
															options={[
																{ label: lang === 'kh' ? 'មករា' : 'January', value: 0 },
																{ label: lang === 'kh' ? 'កុម្ភៈ' : 'February', value: 1 },
																{ label: lang === 'kh' ? 'មីនា' : 'March', value: 2 },
																{ label: lang === 'kh' ? 'មេសា' : 'April', value: 3 },
																{ label: lang === 'kh' ? 'ឧសភា' : 'May', value: 4 },
																{ label: lang === 'kh' ? 'មិថុនា' : 'June', value: 5 },
																{ label: lang === 'kh' ? 'កក្កដា' : 'July', value: 6 },
																{ label: lang === 'kh' ? 'សីហា' : 'August', value: 7 },
																{ label: lang === 'kh' ? 'កញ្ញា' : 'September', value: 8 },
																{ label: lang === 'kh' ? 'តុលា' : 'October', value: 9 },
																{ label: lang === 'kh' ? 'វិច្ឆិកា' : 'November', value: 10 },
																{ label: lang === 'kh' ? 'ធ្នូ' : 'December', value: 11 },
															]}
															required={true}
														/>
													)}
												</>
												<>
													{(values.reportType === mdQuarterlySale?.value ||
														values.reportType === mdQuarterlyClaim?.value ||
														values.reportType === sdQuarterlyFinance?.value ||
														values.reportType === sdQuarterlyInvestment?.value) && (
														<SelectValueFormField
															{...props}
															placeholder={t('Quarter')}
															name="quarter"
															label="Quarter"
															options={[
																{ label: lang === 'kh' ? 'ត្រីមាសទី 1' : 'Quarter 1', value: 1 },
																{ label: lang === 'kh' ? 'ត្រីមាសទី 2' : 'Quarter 2', value: 2 },
																{ label: lang === 'kh' ? 'ត្រីមាសទី 3' : 'Quarter 3', value: 3 },
																{ label: lang === 'kh' ? 'ត្រីមាសទី 4' : 'Quarter 4', value: 4 },
															]}
															required={true}
														/>
													)}
												</>
												<>
													<DateInputOrSelectFormField {...props} name="dueDate" label="Due Date" required={true} />
												</>
												<>
													<FileFormField {...props} label="Attachment" name="attachment" maxFileSize={c.maxFileSize / 20} />
												</>
											</>
										)}
										<>
											<Flex flex="1" mt={4} mb={2} justifyContent="flex-end" alignItems="center">
												<Button
													type="submit"
													isLoading={loading}
													borderRadius={c.borderRadius}
													colorScheme="blue"
													onMouseDown={() => null}
												>
													{t('Create')}
												</Button>
											</Flex>
										</>
									</Box>
								</Form>
							);
						}}
					</Formik>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
}

export default Create;
